import { z } from 'zod';
import { BANNER_LAYOUT_KINDS, BANNER_LAYOUT_ROLES } from './roles';

/**
 * バナーレイアウト JSON (FE / BE 共通の契約・docs/features/banner-layout-template-plan.md §1)。
 * 検証規則は backend/services/banner_layout/schema.py と同じ。
 */
export const BANNER_LAYOUT_MAX_ELEMENTS = 12;
export const BANNER_LAYOUT_MIN_SIZE = 0.02;
export const BANNER_LAYOUT_MAX_TEXT = 300;
export const BANNER_LAYOUT_MAX_LABEL = 20;
/** これを超えたら「多すぎると守られにくくなります」の注意を出す */
export const BANNER_LAYOUT_WARN_ELEMENTS = 8;
/** これを超える文言は崩れやすいので注意を出す */
export const BANNER_LAYOUT_WARN_TEXT = 60;
const EPS = 1e-6;

const finite = z.number().refine((v) => Number.isFinite(v), '有限の数値で指定してください');
/** 要素の傾き / 背景の分割線の傾きの上限 (度)。斜め割り (§16) */
export const BANNER_LAYOUT_MAX_ROTATION = 45;
const degrees = finite.refine((v) => Math.abs(v) <= BANNER_LAYOUT_MAX_ROTATION + EPS, '傾きは -45〜45 度で指定してください');

/**
 * 背景の分割 (レイアウト全体の属性・§16)。省略 = 分割なし。分割線は、縦割りなら (position×W, H/2)、横割りなら (W/2, position×H) を通り、
 * 真下 / 真右の向きを angle 度だけ時計回りに回した向き。写真側 (visualSide) をガイドで濃いグレーに塗る。
 */
export const bannerLayoutBackgroundSchema = z.discriminatedUnion('orientation', [
  z.object({ type: z.literal('split'), orientation: z.literal('vertical'), angle: degrees, position: finite.refine((v) => v >= -EPS && v <= 1 + EPS), visualSide: z.enum(['left', 'right']) }).strict(),
  z.object({ type: z.literal('split'), orientation: z.literal('horizontal'), angle: degrees, position: finite.refine((v) => v >= -EPS && v <= 1 + EPS), visualSide: z.enum(['top', 'bottom']) }).strict(),
]);
export type BannerLayoutBackground = z.infer<typeof bannerLayoutBackgroundSchema>;

export const bannerLayoutElementSchema = z
  .object({
    id: z.string().min(1).max(64),
    kind: z.enum(BANNER_LAYOUT_KINDS),
    label: z.string().max(BANNER_LAYOUT_MAX_LABEL).optional(),
    x: finite,
    y: finite,
    w: finite,
    h: finite,
    text: z.string().max(BANNER_LAYOUT_MAX_TEXT).optional(),
    /** 旧データの互換 (段階1のテンプレート等)。今は「何枚目か」(imageIndex) で指す。素材が今も選択中なら優先する */
    assetId: z.string().min(1).max(64).optional(),
    /** product のみ: 選択中の画像の何枚目か (0始まり・既定0)。素材を差し替えると自動で追従する (§17) */
    imageIndex: z.number().int().min(0).max(BANNER_LAYOUT_MAX_ELEMENTS - 1).optional(),
    /** 傾き (度・時計回りが正・中心が軸)。位置と大きさは回転前の矩形 */
    rotation: degrees.optional(),
  })
  .strict()
  .superRefine((el, ctx) => {
    if (el.w < BANNER_LAYOUT_MIN_SIZE - EPS || el.h < BANNER_LAYOUT_MIN_SIZE - EPS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '要素が小さすぎます' });
    }
    if (el.x < -EPS || el.y < -EPS || el.x + el.w > 1 + EPS || el.y + el.h > 1 + EPS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '要素がキャンバスからはみ出しています' });
    }
    if (!BANNER_LAYOUT_ROLES[el.kind].hasText && el.text) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'ロゴ・商品の要素に文言は入れられません' });
    }
    if (el.kind !== 'product' && (el.assetId !== undefined || el.imageIndex !== undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '商品以外の要素に画像は指定できません' });
    }
  });

/** 'W:H' (W, H は有限の正の数・小数可) → W/H。不正なら null */
export function parseRatio(aspect: string | null | undefined): number | null {
  if (typeof aspect !== 'string') return null;
  const parts = aspect.split(':');
  if (parts.length !== 2 || parts.some((p) => p.trim() === '')) return null;
  const w = Number(parts[0]);
  const h = Number(parts[1]);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w / h;
}

export const bannerLayoutSchema = z
  .object({
    version: z.literal(1),
    aspectRatio: z.string(),
    elements: z.array(bannerLayoutElementSchema).min(1).max(BANNER_LAYOUT_MAX_ELEMENTS),
    background: bannerLayoutBackgroundSchema.optional(),
  })
  .strict()
  .superRefine((layout, ctx) => {
    const r = parseRatio(layout.aspectRatio);
    if (r === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'アスペクト比は W:H の形で指定してください' });
    } else if (r < 1 / 8 - EPS || r > 8 + EPS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'アスペクト比は1:8〜8:1の範囲で指定してください' });
    }
    const ids = new Set<string>();
    layout.elements.forEach((el) => {
      if (ids.has(el.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: '要素の id が重複しています' });
      ids.add(el.id);
    });
    if (new TextEncoder().encode(JSON.stringify(layout)).length > 16 * 1024) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'レイアウトが大きすぎます' });
    }
  });

export type BannerLayoutElement = z.infer<typeof bannerLayoutElementSchema>;
export type BannerLayout = z.infer<typeof bannerLayoutSchema>;

export type BannerLayoutTemplate = {
  id: string;
  name: string;
  aspect_ratio: string;
  layout: BannerLayout;
  updated_at: string;
};

export type BannerLayoutPreset = {
  id: string;
  name: string;
  aspectRatio: string;
  tags: string[];
  layout: BannerLayout;
};
